import React, { useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { exportReportPdf } from './reports/reportPdf';
import DATA from './europcarGooglePuanData.json';

// Europcar · Google ortalama puanı, Ocak 2026 ↔ Ekim 2026 (Meltem Özdemir talebi, 14/20 Eki).
// Güncel puan ve yorum sayısı: GBP günlük fotoğrafı (gbp_daily_stats, Google'ın gösterdiği değer).
// Ocak 2026 puanı: o tarihe kadar yazılmış yorumların yıldız ortalaması (gbp_review_months, kümülatif).
// Google geçmiş tarihli puan vermiyor; bugünkü değeri aynı yöntemle hesaplayınca 4,27 çıkıyor, Google 4,27 gösteriyor.
// Veri yeniden: gbp_review_months + gbp_daily_stats → europcarGooglePuanData.json (elle üretildi).
// 3 Kas: Meltem'in 22 Eki güncel ofis listesi esas (ApiConnection.metaJson.gbpStations) — kapalı ofisler çıktı,
// yorumu olmayan ve Google kaydı erişimimizde olmayan istasyonlar da satır olarak görünür; Goldcar ayrı sekme.

const f2 = (v) => (v == null ? '–' : v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const f1 = (v) => (v == null ? '–' : v.toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
const n0 = (v) => v.toLocaleString('tr-TR');
const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const mLabel = (m) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;

const asOf = new Date(DATA.asOf + 'T12:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });

function summarize(g) {
  const all = g.locs;
  const locs = all.filter((l) => !l.missing && l.nowR > 0);
  const jan = { r: locs.reduce((s, l) => s + l.janR, 0), s: locs.reduce((s, l) => s + (l.janAvg || 0) * l.janR, 0) };
  const now = { r: locs.reduce((s, l) => s + l.nowR, 0), s: locs.reduce((s, l) => s + l.nowAvg * l.nowR, 0) };
  // Sıra: yorumu olanlar (çoktan aza) → kaydı olup yorumu olmayanlar → kaydı erişimde olmayanlar
  const rank = (l) => (l.missing ? 2 : l.nowR ? 0 : 1);
  const rows = [...all].sort((a, b) => rank(a) - rank(b) || (b.nowR || 0) - (a.nowR || 0) || a.title.localeCompare(b.title, 'tr'));
  return {
    label: g.label,
    trend: g.trend,
    rows,
    jan,
    now,
    janAvg: jan.r ? jan.s / jan.r : null,
    nowAvg: now.r ? now.s / now.r : null,
    counts: {
      total: all.length,
      withReviews: locs.length,
      noReviews: all.filter((l) => !l.missing && !l.nowR).length,
      missing: all.filter((l) => l.missing).length,
    },
  };
}

function Kpi({ label, value, sub, accent }) {
  return (
    <div className={`rounded-2xl p-5 ${accent ? 'bg-[#0b1430] text-white' : 'bg-slate-50 text-slate-900'}`}>
      <div className={`text-xs font-semibold tracking-wide ${accent ? 'text-white/70' : 'text-slate-500'}`}>{label}</div>
      <div className="text-3xl font-bold mt-1">{value}</div>
      {sub && <div className={`text-sm mt-1 ${accent ? 'text-white/70' : 'text-slate-500'}`}>{sub}</div>}
    </div>
  );
}

function Delta({ v, digits = 2 }) {
  if (v == null) return <span className="text-slate-400">–</span>;
  const s = v.toLocaleString('tr-TR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const cls = v > 0.004 ? 'text-emerald-600' : v < -0.004 ? 'text-rose-600' : 'text-slate-500';
  return <span className={`font-semibold ${cls}`}>{v > 0 ? '+' : ''}{s}</span>;
}

function exportExcel({ label, trend, rows, jan, now, janAvg, nowAvg }) {
  const wb = XLSX.utils.book_new();
  const ozet = [
    ['', 'Ortalama puan', 'Yorum sayısı'],
    ['Ocak 2026', Number(janAvg.toFixed(2)), jan.r],
    [`Güncel (${asOf})`, Number(nowAvg.toFixed(2)), now.r],
    ['Değişim', Number((nowAvg - janAvg).toFixed(2)), now.r - jan.r],
  ];
  const aylar = [['Ay', 'Ay sonu ortalama puan', 'Ay sonu toplam yorum', 'O ay gelen yorum', 'O ay gelen yorumların puanı'],
    ...trend.map((t) => [mLabel(t.m), Number(t.cumAvg.toFixed(2)), t.cumR, t.newR, t.newAvg == null ? '' : Number(t.newAvg.toFixed(2))])];
  const ofisler = [['Ofis', 'Ocak 2026 puan', 'Ocak 2026 yorum', 'Güncel puan', 'Güncel yorum', 'Not'],
    ...rows.map((l) => (l.missing
      ? [l.title, '', '', '', '', 'Google kaydı bizim erişimimizde değil']
      : [l.title, l.janR ? Number(l.janAvg.toFixed(1)) : '', l.janR, l.nowR ? l.nowAvg : '', l.nowR, l.nowR ? '' : 'Henüz yorum yok']))];
  [['Özet', ozet], ['Aylara göre', aylar], ['Ofis bazında', ofisler]].forEach(([name, aoa]) => {
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = aoa[0].map((_, i) => ({ wch: i === 0 ? 38 : 22 }));
    XLSX.utils.book_append_sheet(wb, ws, name);
  });
  XLSX.writeFile(wb, `${label}-Google-Puan-${DATA.asOf}.xlsx`);
}

export default function EuropcarGooglePuan() {
  const pdfRef = useRef(null);
  const [key, setKey] = useState(DATA.groups[0].key);
  const g = useMemo(() => summarize(DATA.groups.find((x) => x.key === key) || DATA.groups[0]), [key]);
  const { label, trend, rows, jan, now, janAvg, nowAvg, counts } = g;
  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <div className="flex flex-wrap justify-between gap-2 mb-3">
        <div className="inline-flex rounded-xl border border-slate-300 overflow-hidden bg-white">
          {DATA.groups.map((x) => (
            <button key={x.key} onClick={() => setKey(x.key)}
              className={`px-4 py-2 text-sm font-semibold ${key === x.key ? 'bg-[#0b1430] text-white' : 'text-slate-700 hover:bg-slate-50'}`}>
              {x.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
        <button onClick={() => exportExcel(g)} className="px-4 py-2 rounded-xl border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50">Excel</button>
        <button onClick={() => exportReportPdf(pdfRef.current, `${label}-Google-Puan-${DATA.asOf}.pdf`)} className="px-4 py-2 rounded-xl bg-[#0b1430] text-sm font-semibold text-white hover:opacity-90">PDF</button>
        </div>
      </div>
      <div ref={pdfRef}>
      <div className="bg-white rounded-3xl border border-slate-200 p-6 md:p-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{label} · Google Ortalama Puanı</h1>
            <div className="text-slate-500 mt-1">
              Ocak 2026 ile {asOf} karşılaştırması · {counts.total} istasyon
              {counts.withReviews < counts.total && ` (${counts.withReviews} istasyonda yorum var)`}
            </div>
          </div>
          {key === 'europcar' && <img src="/europcar-logo.svg" alt="" className="h-8" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <Kpi label="OCAK 2026 ORTALAMA PUAN" value={f2(janAvg)} sub={`${n0(jan.r)} yorum`} />
          <Kpi label="GÜNCEL ORTALAMA PUAN" value={f2(nowAvg)} sub={`${n0(now.r)} yorum · ${asOf}`} accent />
          <Kpi label="DEĞİŞİM" value={<Delta v={nowAvg - janAvg} />} sub={`+${n0(now.r - jan.r)} yorum`} />
        </div>

        <h2 className="text-lg font-semibold text-slate-900 mt-10 mb-3">Aylara göre</h2>
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Ay</th>
                <th className="text-right px-4 py-2.5 font-semibold">Ay sonu ortalama puan</th>
                <th className="text-right px-4 py-2.5 font-semibold">Ay sonu toplam yorum</th>
                <th className="text-right px-4 py-2.5 font-semibold">O ay gelen yorum</th>
                <th className="text-right px-4 py-2.5 font-semibold">O ay gelen yorumların puanı</th>
              </tr>
            </thead>
            <tbody>
              {trend.map((t) => (
                <tr key={t.m} className="border-t border-slate-100">
                  <td className="px-4 py-2">{mLabel(t.m)}{t.m === DATA.asOf.slice(0, 7) ? ` (${asOf.split(' ').slice(0, 2).join(' ')}'a kadar)` : ''}</td>
                  <td className="px-4 py-2 text-right font-semibold">{f2(t.cumAvg)}</td>
                  <td className="px-4 py-2 text-right">{n0(t.cumR)}</td>
                  <td className="px-4 py-2 text-right">{n0(t.newR)}</td>
                  <td className="px-4 py-2 text-right">{f2(t.newAvg)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="text-lg font-semibold text-slate-900 mt-10 mb-3">Ofis bazında</h2>
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Ofis</th>
                <th className="text-right px-4 py-2.5 font-semibold">Ocak 2026 puan</th>
                <th className="text-right px-4 py-2.5 font-semibold">Ocak 2026 yorum</th>
                <th className="text-right px-4 py-2.5 font-semibold">Güncel puan</th>
                <th className="text-right px-4 py-2.5 font-semibold">Güncel yorum</th>
                <th className="text-right px-4 py-2.5 font-semibold">Puan farkı</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => (l.missing || !l.nowR ? (
                <tr key={l.title} className="border-t border-slate-100 text-slate-400">
                  <td className="px-4 py-2">{l.title}</td>
                  <td colSpan={5} className="px-4 py-2 text-right text-xs">
                    {l.missing ? 'Google kaydı erişimimizde değil' : 'Henüz yorum yok'}
                  </td>
                </tr>
              ) : (
                <tr key={l.title} className="border-t border-slate-100">
                  <td className="px-4 py-2">{l.title}</td>
                  <td className="px-4 py-2 text-right">{l.janR ? f1(l.janAvg) : '–'}</td>
                  <td className="px-4 py-2 text-right">{n0(l.janR)}</td>
                  <td className="px-4 py-2 text-right font-semibold">{f1(l.nowAvg)}</td>
                  <td className="px-4 py-2 text-right">{n0(l.nowR)}</td>
                  <td className="px-4 py-2 text-right"><Delta v={l.janR ? Math.round(l.nowAvg * 10) / 10 - Math.round(l.janAvg * 10) / 10 : null} digits={1} /></td>
                </tr>
              )))}
            </tbody>
            <tfoot className="bg-slate-50 font-semibold">
              <tr className="border-t border-slate-200">
                <td className="px-4 py-2.5">Toplam / ortalama</td>
                <td className="px-4 py-2.5 text-right">{f2(janAvg)}</td>
                <td className="px-4 py-2.5 text-right">{n0(jan.r)}</td>
                <td className="px-4 py-2.5 text-right">{f2(nowAvg)}</td>
                <td className="px-4 py-2.5 text-right">{n0(now.r)}</td>
                <td className="px-4 py-2.5 text-right"><Delta v={nowAvg - janAvg} /></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <p data-pdf-block className="text-xs text-slate-500 mt-6 leading-relaxed">
          Güncel puan ve yorum sayısı Google İşletme Profili'nin {asOf} tarihli değeridir. Google geçmiş tarihli puan paylaşmadığı için
          Ocak 2026 puanı, 31 Ocak 2026'ya kadar yazılmış yorumların yıldız ortalamasıyla hesaplanmıştır. Aynı yöntem bugün için de
          Google'ın gösterdiği puanı verir. Ofis puanları Google'daki gibi tek haneli gösterilir. Genel ortalama, ofislerin yorum sayısına göre ağırlıklıdır. Silinmiş yorumlar hesaba girmez.
          {' '}İstasyon listesi {label}'ın 22 Ekim 2026'da paylaştığı güncel ofis listesidir; kapalı ofisler rapora alınmamıştır.
          {counts.missing > 0 && ` ${counts.missing} istasyonun Google kaydı bizim yönetim erişimimizde olmadığı için verisi gösterilemiyor.`}
        </p>
      </div>
      </div>
    </div>
  );
}
